'use client';

import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

export type Theme = 'system' | 'light' | 'dark';
type ResolvedTheme = 'light' | 'dark';

/**
 * Color palettes are an opt-in layer ON TOP of light/dark mode.
 * - 'default'     — Advocate's Lamp: emerald glass + brass over warm ivory
 *                   (no DOM attribute — base tokens in :root / .dark apply).
 * - 'calm'        — muted sage + warm-stone palette; data-palette="calm".
 * - 'futuristic'  — original Lexomat purple/blue techy identity;
 *                   data-palette="futuristic".
 * - 'warm'        — claude.ai-inspired warm cream + coral/clay accent;
 *                   data-palette="warm".
 *
 * Selecting a non-default palette sets `data-palette="<name>"` on <html>,
 * which globals.css uses to override brand/surface tokens for both light
 * and dark mode.
 */
export const PALETTES = ['default', 'calm', 'futuristic', 'warm'] as const;
export type Palette = (typeof PALETTES)[number];

interface ThemeContextValue {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
  palette: Palette;
  setPalette: (palette: Palette) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const STORAGE_KEY = 'lexomat_user_theme';
export const PALETTE_STORAGE_KEY = 'lexomat_user_palette';

/**
 * Coerce any stored/incoming value to a known palette. Unknown values (legacy,
 * tampered localStorage, future palettes not yet shipped) fall back to
 * 'default' so a user is never stranded on a palette the CSS doesn't define.
 */
export const normalizePalette = (value: string | null | undefined): Palette =>
  (PALETTES as readonly string[]).includes(value ?? '')
    ? (value as Palette)
    : 'default';

// Helper to get resolved theme from system preference
const getSystemTheme = (): ResolvedTheme => {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
};

// Helper to apply theme to DOM
const applyThemeToDOM = (resolvedTheme: ResolvedTheme) => {
  const root = document.documentElement;
  root.classList.remove('light', 'dark');
  root.classList.add(resolvedTheme);
  root.style.colorScheme = resolvedTheme;
};

// Helper to apply palette to DOM. 'default' carries no attribute so the base
// tokens in :root / .dark apply unchanged.
const applyPaletteToDOM = (palette: Palette) => {
  const root = document.documentElement;
  if (palette === 'default') {
    root.removeAttribute('data-palette');
  } else {
    root.setAttribute('data-palette', palette);
  }
};

interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * Simple theme provider that replaces next-themes.
 * - Uses localStorage key 'lexomat_user_theme'
 * - Applies .light or .dark class to <html>
 * - Supports 'system' theme that follows OS preference
 */
export function ThemeProvider({ children }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>('system');
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>('light');
  const [palette, setPaletteState] = useState<Palette>('default');
  const [mounted, setMounted] = useState(false);

  // Initialize theme + palette from localStorage on mount
  useEffect(() => {
    setMounted(true);

    const savedTheme = localStorage.getItem(STORAGE_KEY) as Theme | null;
    const initialTheme: Theme =
      savedTheme === 'light' || savedTheme === 'dark' || savedTheme === 'system'
        ? savedTheme
        : 'system';

    setThemeState(initialTheme);

    const resolved =
      initialTheme === 'system' ? getSystemTheme() : initialTheme;
    setResolvedTheme(resolved);
    applyThemeToDOM(resolved);

    const initialPalette = normalizePalette(
      localStorage.getItem(PALETTE_STORAGE_KEY)
    );
    setPaletteState(initialPalette);
    applyPaletteToDOM(initialPalette);
  }, []);

  // Listen for system preference changes when theme is 'system'
  useEffect(() => {
    if (!mounted || theme !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      const newResolved = getSystemTheme();
      setResolvedTheme(newResolved);
      applyThemeToDOM(newResolved);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [mounted, theme]);

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem(STORAGE_KEY, newTheme);

    const resolved = newTheme === 'system' ? getSystemTheme() : newTheme;
    setResolvedTheme(resolved);
    applyThemeToDOM(resolved);
  }, []);

  const setPalette = useCallback((newPalette: Palette) => {
    setPaletteState(newPalette);
    localStorage.setItem(PALETTE_STORAGE_KEY, newPalette);
    applyPaletteToDOM(newPalette);
  }, []);

  // Provide default values during SSR
  const value: ThemeContextValue = {
    theme: mounted ? theme : 'system',
    resolvedTheme: mounted ? resolvedTheme : 'light',
    setTheme,
    palette: mounted ? palette : 'default',
    setPalette,
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

/**
 * Hook to access theme context.
 * Returns { theme, resolvedTheme, setTheme }
 */
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

/**
 * ThemeSync is no longer needed - kept for backward compatibility.
 */
export function ThemeSync() {
  return null;
}
